import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DialogActions,
  DialogContentText,
  Typography,
} from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid';

import Modal from 'src/components/common/Modal';

export interface ShareModalConfirmationModalProps {
  initialSharedWithIds: GridRowSelectionModel;
  filteredSharedWithIds?: GridRowSelectionModel;
  fullSelectionCount?: number;
  isOpen: boolean;
  isReport: boolean;
  isList: boolean;
  message?: string;
  onClose: () => void;
  onConfirmation: () => void;
  sharedWithIds: GridRowSelectionModel;
}

const ShareModalConfirmationModal: FC<ShareModalConfirmationModalProps> = ({
  initialSharedWithIds = { type: 'include', ids: new Set() },
  filteredSharedWithIds = { type: 'include', ids: new Set() },
  isOpen,
  isReport,
  isList,
  message,
  onClose,
  onConfirmation,
  sharedWithIds = { type: 'include', ids: new Set() },
  fullSelectionCount,
}) => {
  const { t } = useTranslation(['report', 'common']);
  const itemType = isReport ? 'report' : 'dataLine';

  const initialSharedWithIdsArray = Array.from(initialSharedWithIds.ids);
  const filteredSharedWithIdsArray = Array.from(filteredSharedWithIds.ids);
  const sharedWithIdsArray = Array.from(sharedWithIds.ids);

  const headline = `${
    initialSharedWithIdsArray.length === 0
      ? t('reportInfo.shareConfirmation', {
          itemType: isList
            ? t(itemType, { count: 2 })
            : t(itemType, { count: 1 }),
          count: isList ? 2 : 1,
        })
      : t('reportInfo.updateConfirmation', {
          itemType: isList
            ? t(itemType, { count: 2 })
            : t(itemType, { count: 1 }),
          count: isList ? 2 : 1,
        })
  }`;

  let startSharingAmount: number;
  let continueSharingAmount: number;
  let stopSharingAmount: number;

  // check if fullSelectionCount is defined
  if (typeof fullSelectionCount === 'number') {
    if (isList) {
      startSharingAmount = fullSelectionCount;
      continueSharingAmount = 0;
      stopSharingAmount = 0;
    } else {
      startSharingAmount =
        fullSelectionCount - filteredSharedWithIdsArray.length;
      continueSharingAmount = initialSharedWithIdsArray.length;
      stopSharingAmount = 0;
    }
  } else {
    startSharingAmount = sharedWithIdsArray.filter(
      (sharedWithId) => !initialSharedWithIds.ids.has(sharedWithId),
    ).length;
    continueSharingAmount = initialSharedWithIdsArray.filter(
      (initialSharedWithId) => sharedWithIds.ids.has(initialSharedWithId),
    ).length;
    stopSharingAmount = initialSharedWithIdsArray.filter(
      (initialSharedWithId) => !sharedWithIds.ids.has(initialSharedWithId),
    ).length;
  }

  return (
    <Modal
      headline={headline}
      message={message}
      isOpen={isOpen}
      onClose={() => onClose()}
    >
      {initialSharedWithIdsArray.length === 0 ? (
        <DialogContentText>
          <Typography>
            {t('reportInfo.shareText', {
              itemType: isList
                ? t(itemType, { count: 2 })
                : t(itemType, { count: 1 }),
              amount:
                startSharingAmount === null
                  ? t('reportInfo.multiple')
                  : startSharingAmount,
              users: t('reportInfo.user', { count: startSharingAmount }),
              count: isList ? 2 : 1,
            })}
          </Typography>
        </DialogContentText>
      ) : (
        <DialogContentText>
          <Typography>
            {t('reportInfo.additionalShareText', {
              itemType: isList
                ? t(itemType, { count: 2 })
                : t(itemType, { count: 1 }),
              amount:
                startSharingAmount === null
                  ? t('reportInfo.multiple')
                  : startSharingAmount,
              count: isList ? 2 : 1,
              users: t('reportInfo.user', { count: startSharingAmount }),
            })}
            .<br />
            {t('reportInfo.stopShareText', {
              itemType: isList
                ? t(itemType, { count: 2 })
                : t(itemType, { count: 1 }),
              amount: stopSharingAmount,
              count: isList ? 2 : 1,
              users: t('reportInfo.user', { count: stopSharingAmount }),
            })}
            .<br />
            {t('reportInfo.continueShareText', {
              itemType: isList
                ? t(itemType, { count: 2 })
                : t(itemType, { count: 1 }),
              amount: continueSharingAmount,
              count: isList ? 2 : 1,
              users: t('reportInfo.user', { count: continueSharingAmount }),
            })}
            .<br />
          </Typography>
        </DialogContentText>
      )}

      <DialogActions>
        <Button
          variant="cta"
          color="secondary"
          fullWidth
          onClick={() => onClose()}
        >
          {t('common:cancel')}
        </Button>

        <Button variant="cta" fullWidth onClick={() => onConfirmation()}>
          {message ? t('common:tryAgain') : t('common:confirm')}
        </Button>
      </DialogActions>
    </Modal>
  );
};

export default ShareModalConfirmationModal;
